"use client";

import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  BookOpen,
  ChevronDown,
  FolderOpen,
  Lightbulb,
  Database,
  Bot,
  Link2,
  Sparkles,
} from "lucide-react";

export function HowToGuide() {
  const [open, setOpen] = useState(false);

  const steps: Array<{
    icon: React.ComponentType<{ className?: string }>;
    color: string;
    title: string;
    text: string;
  }> = [
    {
      icon: FolderOpen,
      color: "text-blue-400",
      title: "Mit einem Beispiel starten",
      text: "Klicke auf + Beispiel für einen Flow aus Signal, LLM-Schritt und Zustand. Oder lege eigene Schritte über die drei Buttons an.",
    },
    {
      icon: Lightbulb,
      color: "text-orange-400",
      title: "Interaktion erfassen",
      text: "Öffne ein Signal und beschreibe Ereignis, Quelle und Beispiel-Payload. Etwa: Eine Person sendet im Chat eine Nachricht ab.",
    },
    {
      icon: Database,
      color: "text-violet-400",
      title: "Zustände festhalten",
      text: "Ein Zustand dokumentiert einen Wert wie loading oder answered. Eintritts- und Austrittsbedingungen erklären, wann er aktiv sein soll. Datenmodelle kannst du separat im Datenbank-Tab skizzieren.",
    },
    {
      icon: Bot,
      color: "text-emerald-400",
      title: "LLM Prompt strukturieren",
      text: "Wähle bei einer Aktion LLM Prompt. Definiere System-Prompt, User-Template und Ports. Im Live-Test ersetzt {{input}} den gesamten Test-Input; weitere Platzhalter werden nicht automatisch aufgelöst.",
    },
    {
      icon: Link2,
      color: "text-gray-400",
      title: "Nodes verknüpfen",
      text: "Ziehe eine Verbindung vom rechten Anschluss eines Schritts zum linken Anschluss des nächsten. Verbindungen modellieren den Ablauf; sie führen ihn noch nicht automatisch aus.",
    },
    {
      icon: Sparkles,
      color: "text-violet-400",
      title: "Mit Gemini live testen",
      text: "Trage einen Test-Input ein und klicke auf Schritt testen. Die Antwort erscheint während der Generierung. Die letzten fünf Tests inklusive Prompt und Fehlerstatus bleiben am Schritt gespeichert.",
    },
  ];

  return (
    <div className="mt-6 rounded-2xl border border-gray-200 dark:border-white/10">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-3 px-5 py-4"
      >
        <div className="flex items-center gap-2.5">
          <BookOpen className="h-5 w-5 text-gray-400" />
          <span className="font-heading text-base font-bold text-black dark:text-white">
            Wie funktioniert das?
          </span>
        </div>
        <ChevronDown
          className={`h-5 w-5 text-gray-400 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.22 }}
            className="overflow-hidden"
          >
            <div className="grid grid-cols-1 gap-4 border-t border-gray-100 px-5 py-5 dark:border-white/5 sm:grid-cols-2 xl:grid-cols-3">
              {steps.map((step, i) => {
                const StepIcon = step.icon;
                return (
                  <div
                    key={i}
                    className="dark:bg-white/3 flex gap-3 rounded-xl border border-gray-100 bg-gray-50/50 p-4 dark:border-white/5"
                  >
                    <div className={`mt-0.5 shrink-0 ${step.color}`}>
                      <StepIcon className="h-5 w-5" />
                    </div>
                    <div>
                      <p className="mb-1 text-sm font-semibold text-black dark:text-white">
                        {i + 1}. {step.title}
                      </p>
                      <p className="text-xs leading-relaxed text-gray-500 dark:text-gray-400">
                        {step.text}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="border-t border-gray-100 px-5 py-3 dark:border-white/5">
              <p className="text-xs text-gray-400">
                💾 Alles wird automatisch im{" "}
                <code className="font-mono">localStorage</code> gespeichert –
                Live-Tests senden den jeweiligen Prompt an Gemini.
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
